import { useMemo, useState, type FormEvent } from 'react';
import type { CostStatus } from '../api/types.js';
import { useCostThreshold, useSetCostThreshold } from '../hooks/use-cost-threshold.js';
import { useServerCosts } from '../hooks/use-costs.js';
import { Button } from './button.js';
import { SectionAction, SettingsPanel, SettingsSection } from './settings-section.js';

// « Monthly cost » of a server: this month's spend so far and where it is heading, against its threshold on a bar
// of the month. Cost Explorer data is 24-48 h late, hence « as of ». Passing the threshold raises an urgent decision
// in the Waiting on you list. Without a threshold (or before its first daily check) the month so far is read from
// the same costs the Costs page uses, so the threshold is set knowing the month.

const usd = (n: number) => `$${n < 100 ? n.toFixed(2).replace(/\.00$/, '') : Math.round(n).toLocaleString('en-US')}`;
const asOf = (iso: string) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(iso));

function daysIn(d: Date): number {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
}

/** Spend per elapsed day, carried to the end of the month (data up to `dataAsOf`) */
export function projection(costUsd: number, dataAsOf: string): number | null {
  const d = new Date(dataAsOf);
  const elapsed = d.getUTCDate() - 1 + d.getUTCHours() / 24;
  if (elapsed < 2) return null; // too early in the month to say
  return (costUsd / elapsed) * daysIn(d);
}

export function costSentence(s: CostStatus): string {
  if (!s.threshold || !s.monthToDate) return '';
  const { costUsd, dataAsOf } = s.monthToDate;
  const head = `${usd(costUsd)} of ${usd(s.threshold.monthlyUsd)} this month, as of ${asOf(dataAsOf)}.`;
  if (costUsd >= s.threshold.monthlyUsd) return `Over the threshold: ${head}`;
  const p = projection(costUsd, dataAsOf);
  if (!p) return head;
  return `${head} At this pace, about ${usd(p)} by the end of ${monthName(dataAsOf)}${p >= s.threshold.monthlyUsd ? ', past the threshold' : ''}.`;
}

const monthName = (iso: string) => new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(new Date(iso));

/** The month so far without a threshold to compare with: « $12.40 so far this month, as of 10 Oct. At this pace… » */
export function soFarSentence(costUsd: number, dataAsOf: string): string {
  const head = `${usd(costUsd)} so far this month, as of ${asOf(dataAsOf)}.`;
  const p = projection(costUsd, dataAsOf);
  return p ? `${head} At this pace, about ${usd(p)} by the end of ${monthName(dataAsOf)}.` : head;
}

/** This month in UTC up to tomorrow, as the daily cost check reads it; stable for the day so the query is too */
function useMonthSoFar(): { since: Date; until: Date } {
  const today = new Date().toISOString().slice(0, 10);
  return useMemo(() => {
    const d = new Date(`${today}T00:00:00Z`);
    return {
      since: new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)),
      until: new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1)),
    };
  }, [today]);
}

function MonthBar({ costUsd, thresholdUsd, dataAsOf }: { costUsd: number; thresholdUsd: number; dataAsOf: string }) {
  const ratio = costUsd / thresholdUsd;
  const d = new Date(dataAsOf);
  const elapsed = Math.min(1, (d.getUTCDate() - 1) / daysIn(d));
  const colour = ratio >= 1 ? 'var(--color-error)' : ratio >= 0.8 ? 'var(--color-warn)' : 'var(--color-brand)';
  // Past the threshold the bar keeps growing beyond the threshold mark, so the overspend shows
  const scale = Math.max(1, ratio);
  return (
    <div className="relative h-8" aria-hidden="true">
      <div className="absolute left-0 top-3 h-2 rounded-sm" style={{ width: `${100 / scale}%`, backgroundColor: 'var(--color-surface-3)' }} />
      <div className="absolute left-0 top-3 h-2 rounded-sm" style={{ width: `${(Math.min(ratio, 1) / scale) * 100}%`, backgroundColor: colour }} />
      {ratio > 1 && (
        // the overspend, hatched, beyond the threshold mark
        <div className="absolute top-3 h-2 rounded-r-sm" style={{
          left: `${100 / scale}%`, width: `${((ratio - 1) / scale) * 100}%`,
          backgroundImage: 'repeating-linear-gradient(135deg, var(--color-error) 0 4px, transparent 4px 7px)',
        }} />
      )}
      {/* the threshold */}
      <span className="absolute top-1 h-6 w-px" style={{ left: `${100 / scale}%`, backgroundColor: 'var(--color-text-2)' }} />
      {/* how far into the month the data goes */}
      <span className="absolute top-[0.55rem] h-4 w-px" style={{ left: `${(elapsed / scale) * 100}%`, backgroundColor: 'var(--color-text-3)' }} />
      <span className="absolute top-6 -translate-x-1/2 text-[10px] tabular-nums" style={{ left: `${(elapsed / scale) * 100}%`, color: 'var(--color-text-3)' }}>{asOf(dataAsOf)}</span>
      <span className="absolute top-6 -translate-x-full text-[10px] tabular-nums" style={{ left: `${100 / scale}%`, color: 'var(--color-text-2)' }}>{usd(thresholdUsd)}</span>
    </div>
  );
}

function ThresholdForm({ serverId, current, onDone }: { serverId: string; current: number | null; onDone: () => void }) {
  const save = useSetCostThreshold(serverId);
  const [value, setValue] = useState(current ? String(current) : '');
  const n = Number(value);
  const valid = Number.isFinite(n) && n > 0 && n <= 100_000;
  function submit(e: FormEvent) {
    e.preventDefault();
    if (valid) save.mutate(n, { onSuccess: onDone });
  }
  return (
    <form onSubmit={submit} className="mt-3 flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-1 text-sm" style={{ color: 'var(--color-text)' }}>
        $
        <input type="number" min={1} max={100000} step="any" inputMode="decimal" value={value} autoFocus
          onChange={(e) => setValue(e.target.value)} aria-label="Monthly threshold in dollars"
          className="w-24 rounded px-2 py-1 text-sm tabular-nums"
          style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', color: 'var(--color-text)' }} />
        a month
      </label>
      <Button type="submit" size="sm" disabled={!valid || save.isPending}>{save.isPending ? 'Saving…' : 'Save threshold'}</Button>
      <Button type="button" size="sm" variant="ghost" onClick={onDone}>Cancel</Button>
      {current !== null && (
        <button type="button" disabled={save.isPending} onClick={() => save.mutate(null, { onSuccess: onDone })}
          className="text-xs hover:underline" style={{ color: 'var(--color-text-3)' }}>
          Remove the threshold
        </button>
      )}
      {save.isError && <p role="alert" className="basis-full text-xs" style={{ color: 'var(--color-error)' }}>Not saved: {(save.error as Error).message}</p>}
    </form>
  );
}

export function MonthlyCost({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { data, error, isLoading } = useCostThreshold(serverId);
  const [editing, setEditing] = useState(false);
  const t = data?.threshold ?? null;
  const mtd = data?.monthToDate ?? null;
  // No daily check to read: the month so far from Cost Explorer, kept for an hour (each call is billed)
  const { since, until } = useMonthSoFar();
  const soFar = useServerCosts(serverId, since, until, { enabled: !!data && !mtd, staleTime: 3_600_000 });
  const over = !!t && !!mtd && mtd.costUsd >= t.monthlyUsd;
  const action = isAdmin && data && !editing
    ? <SectionAction onClick={() => setEditing(true)}>{t ? 'Change threshold' : 'Set a threshold'}</SectionAction>
    : undefined;

  if (!data) {
    return (
      <SettingsSection id="cost-title" title="Monthly cost" loading={isLoading} tone="error"
        state={error ? `Cost threshold not loaded: ${(error as Error).message}` : undefined} />
    );
  }

  let state: string | undefined;
  let tone: 'normal' | 'error' = 'normal';
  if (mtd && t) {
    state = costSentence(data);
    if (over) tone = 'error';
  } else if (soFar.data) {
    state = soFarSentence(soFar.data.breakdown.total, soFar.data.dataAsOf);
  } else if (soFar.error) {
    state = `This month's cost not loaded: ${(soFar.error as Error).message}`;
  }
  const note = !t
    ? 'No monthly threshold. With one, an urgent decision appears in "Waiting on you" when this month\'s costs pass it.'
    : !mtd
      ? `Threshold ${usd(t.monthlyUsd)} a month. This month's costs are checked against it once a day; the first check comes shortly.`
      : null;

  return (
    <SettingsSection id="cost-title" title="Monthly cost" action={action} state={state} tone={tone} loading={!state && soFar.isLoading}>
      {note && <p className="mt-1 max-w-[68ch] text-sm" style={{ color: 'var(--color-text-3)' }}>{note}</p>}
      {editing && <ThresholdForm serverId={serverId} current={t?.monthlyUsd ?? null} onDone={() => setEditing(false)} />}
      {t && mtd && (
        <SettingsPanel style={over ? { borderLeft: '3px solid var(--color-error)' } : undefined}>
          <MonthBar costUsd={mtd.costUsd} thresholdUsd={t.monthlyUsd} dataAsOf={mtd.dataAsOf} />
        </SettingsPanel>
      )}
    </SettingsSection>
  );
}
